/** 打印模板 tableConfig 默认值（与 PrintPreview / 模板编辑器一致） */
export const DEFAULT_PRINT_TABLE_CONFIG = {
  borderStyle: 'all',
  showTotalsRow: true,
  columns: [],
  rowHeightMode: 'auto',
  rowHeight: 28,
  cellPaddingX: 6,
  cellPaddingY: 6,
  /** 账单/对账单：每页最多明细行数，0=不限制（由浏览器/纸张自然分页） */
  rowsPerPage: 0,
  /**
   * 开启时：打印/预览仍保留单价、金额、染费、借贷等列与表头，仅单元格数值不显示（备注等文本列照常，如「染费一个」）。
   * 仅当显式为 false 时在纸上显示上述金额类数值。缺省为 true（新建模板）。
   */
  printHideMoneyColumns: true,
  /** 表格边框线宽（px），默认 0.75；PDF/高分辨率打印时 1px 易显粗 */
  borderWidthPx: 0.75
}

export function mergeTableConfigFromRaw(rawTc) {
  const raw = rawTc || {}
  const m = {
    ...DEFAULT_PRINT_TABLE_CONFIG,
    ...raw,
    columns: raw.columns || []
  }
  if (m.rowHeightMode !== 'fixed' && m.rowHeightMode !== 'auto') {
    m.rowHeightMode = Number(m.rowHeight) > 0 ? 'fixed' : 'auto'
  }
  const rh = Number(m.rowHeight)
  m.rowHeight = Number.isFinite(rh) && rh >= 0 ? rh : 28
  const rpp = Math.floor(Number(m.rowsPerPage))
  m.rowsPerPage = Number.isFinite(rpp) && rpp > 0 ? rpp : 0
  if (typeof raw.printHideMoneyColumns !== 'boolean') {
    m.printHideMoneyColumns = false
  }
  const bw = Number(m.borderWidthPx ?? m.borderWidth)
  m.borderWidthPx =
    Number.isFinite(bw) && bw > 0 ? Math.min(3, Math.max(0.25, Math.round(bw * 100) / 100)) : 0.75
  return m
}

/** 表格边框线宽（px） */
export function resolveTableBorderWidthPx (tableConfig) {
  const raw = tableConfig?.borderWidthPx ?? tableConfig?.borderWidth
  if (raw === '' || raw == null) return 0.75
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return 0.75
  return Math.min(3, Math.max(0.25, Math.round(n * 100) / 100))
}

/** 按 borderStyle 返回 CSS border 值 */
export function tableBorderLine (tableConfig, borderStyleOverride) {
  const bs = borderStyleOverride ?? tableConfig?.borderStyle ?? 'all'
  if (bs === 'none') return 'none'
  const w = resolveTableBorderWidthPx(tableConfig)
  return `${w}px solid #000`
}

/**
 * 账单模板：将明细行按每页行数切分，便于表头重复与强制换页；其它类型或未设置则单块。
 */
export function splitTableItemRows (templateType, rowsPerPage, items) {
  const list = Array.isArray(items) ? items : []
  const per = Math.floor(Number(rowsPerPage)) || 0
  if (templateType !== 'payment_request' || per < 1) {
    return [list]
  }
  if (!list.length) {
    return [[]]
  }
  const out = []
  for (let i = 0; i < list.length; i += per) {
    out.push(list.slice(i, i + per))
  }
  return out
}

/**
 * 将模版权重（列宽 %）归一化到合计 100%，与 table-layout:fixed 一起保证打印/预览按设计比例分栏。
 * 无有效权重时按列数均分。
 */
export function getNormalizedTableColumnWidthPercents(columns) {
  const list = Array.isArray(columns) ? columns : []
  if (!list.length) return []
  const n = list.length
  const raw = list.map((c) => {
    const w = Number(c?.width)
    return Number.isFinite(w) && w > 0 ? w : 0
  })
  const sum = raw.reduce((a, b) => a + b, 0)
  if (sum <= 0) {
    const eq = 100 / n
    return list.map(() => eq)
  }
  return raw.map((w) => (w / sum) * 100)
}

export const tableCellTextWrapStyle = {
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  verticalAlign: 'top'
}

/** 明细表表头：白底、无阴影（预览/打印/导出一致，便于实体打印） */
export const printTableHeaderCellStyle = {
  background: '#fff',
  backgroundColor: '#fff',
  boxShadow: 'none',
  WebkitPrintColorAdjust: 'exact',
  printColorAdjust: 'exact'
}

const TABLE_TEXT_ALIGNS = ['left', 'center', 'right']

/** 表头文字对齐：显式 headerAlign 优先，否则与列「内容对齐」align 一致（兼容旧模板） */
export function effectiveTableHeaderTextAlign(col) {
  const h = col?.headerAlign
  if (TABLE_TEXT_ALIGNS.includes(h)) return h
  return col?.align || 'left'
}

/** 模板「锁定行高 / 不锁定行高」；无 rowHeightMode 时仍按 rowHeight>0 视为锁定（旧数据） */
export function isTableRowHeightLocked(tc) {
  const t = tc || {}
  if (t.rowHeightMode === 'auto') return false
  if (t.rowHeightMode === 'fixed') return Number(t.rowHeight) > 0
  return Number(t.rowHeight) > 0
}

/**
 * 开启「金额类列打印」时，这些字段的单元格数值不打印（表头仍保留）；不含 notes/line_remark 等备注列。
 */
const MONEY_CELL_FIELDS_BLANK_WHEN_HIDE = new Set([
  'unit_price',
  'dye_fee',
  'line_amount',
  'item_amount',
  'amount',
  'debit',
  'credit',
  'balance'
])

/**
 * 表格明细/合计：是否在打印纸上留空该字段的数值（备注里的染费说明等不受影响）。
 */
export function shouldBlankMoneyCellForPrint(field, tableConfig) {
  if (!tableConfig || tableConfig.printHideMoneyColumns === false) return false
  const f = String(field ?? '').trim()
  if (!f) return false
  return MONEY_CELL_FIELDS_BLANK_WHEN_HIDE.has(f)
}

/**
 * 参与打印的表格列（与模板列一致；金额是否显示数值由 shouldBlankMoneyCellForPrint 控制）。
 */
export function getTableColumnsForPrint(columns) {
  const cols = Array.isArray(columns) ? columns : []
  return cols.slice()
}

/** 列标题归一化：相邻相同标题合并表头时用于比较（忽略全角空格与多余空白） */
export function normalizeTableColumnHeaderLabel(label) {
  return String(label ?? '')
    .replace(/\u3000/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * 相邻列标题相同时合并表头（类似 Excel 合并单元格，仅表头合并，数据行仍按列显示）。
 * @returns {{ startIndex: number, colspan: number, label: string, col: object }[]}
 */
export function getMergedTableHeaderCells(columns) {
  const cols = Array.isArray(columns) ? columns : []
  if (!cols.length) return []
  const cells = []
  let i = 0
  while (i < cols.length) {
    const col = cols[i]
    const key = normalizeTableColumnHeaderLabel(col?.label)
    let j = i + 1
    while (j < cols.length && normalizeTableColumnHeaderLabel(cols[j]?.label) === key) {
      j += 1
    }
    cells.push({
      startIndex: i,
      colspan: j - i,
      label: col?.label ?? '',
      col
    })
    i = j
  }
  return cells
}

/** 合并表头单元格宽度 = colspan 覆盖列的宽度百分比之和 */
export function mergedTableHeaderCellWidthPercent(columns, startIndex, colspan) {
  const pcts = getNormalizedTableColumnWidthPercents(columns)
  let sum = 0
  for (let i = startIndex; i < startIndex + colspan && i < pcts.length; i += 1) {
    sum += pcts[i] || 0
  }
  return sum
}

/** 表格设置 UI：相邻相同列标题纵向合并（rowspan） */
export function tableHeaderLabelRowSpanAt(columns, rowIndex) {
  const cols = Array.isArray(columns) ? columns : []
  const idx = Number(rowIndex)
  if (!Number.isFinite(idx) || idx < 0 || idx >= cols.length) {
    return { rowspan: 1, colspan: 1 }
  }
  const groups = getMergedTableHeaderCells(cols)
  for (const group of groups) {
    const end = group.startIndex + group.colspan
    if (idx >= group.startIndex && idx < end) {
      if (idx === group.startIndex) {
        return { rowspan: group.colspan, colspan: 1 }
      }
      return { rowspan: 0, colspan: 0 }
    }
  }
  return { rowspan: 1, colspan: 1 }
}

/** 打印表头 colspan>1 时：水平/垂直居中，且不强制单列 width（交给 colgroup + colspan） */
export function mergedTableHeaderPresentationStyle(colspan) {
  if (!colspan || colspan <= 1) return null
  return {
    textAlign: 'center',
    verticalAlign: 'middle'
  }
}
